import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  StyleSheet, Text, TextInput, Pressable, View, Platform, ScrollView,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../../../constants/Colors';
import DeleteIcon24 from '../../../../../../24pxIcons/DeleteIcon24';
import PriorityFlag from '../../../../../../16pxIcons/PriorityFlag';
import Switch from '../../../../../../Switch';
import PlusIcon20 from '../../../../../../24pxIcons/PlusIcon';
import DropdownIcon from '../../../../../../16pxIcons/DropdownIcon';
import Style from '../../../../../../../constants/Style';
import isMobile from '../../../../../../IsMobile';
import getPriorityColor from '../../../../../../PriorityColorScheme';
import SelectionModal from '../../../../../../SelectionModal';
import { logEvent } from '../../../../../../LogEvent';
import {
  TemplateIssueCategory, TemplateIssueCategoryTitles, UserGroup,
} from '../../../../../../../constants/Types';
import PriorityList from './components/PriorityList';
import AddGroupList from './components/AddGroupList';
import UserGroupTag from './components/UserGroupTag';
import CategoryList from './components/CategoryList';
import PressableStyled from '../../../../../../PressableStyled';
import ReorderIcon24 from '../../../../../../24pxIcons/ReorderIcon24';
import AssignUserGroupButton from './components/AssignUserGroupButton';

interface IssueRowProps {
  groups: UserGroup[],
  title: string,
  tags: string[],
  priority: number,
  enabled: boolean,
  deleteIssue: () => void,
  updateIssue,
  priorityDropdownShownIndex: number | null,
  setPriorityDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  issueTagDropdownShownIndex: number | null,
  setIssueTagDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  tagDropdownShownIndex: number,
  setTagDropdownShownIndex: Dispatch<SetStateAction<number>>,
  index: number,
  slaNames: string[],
  category: TemplateIssueCategory,
  categoryDropdownShownIndex: number | null,
  setCategoryDropdownShownIndex: Dispatch<SetStateAction<number | null>>,
  draggableProps?: { [key: string]: any },
}

const IssueRow = ({
  title, tags, priority, updateIssue, deleteIssue, enabled = true, groups,
  index, setIssueTagDropdownShownIndex, priorityDropdownShownIndex, setPriorityDropdownShownIndex,
  issueTagDropdownShownIndex, tagDropdownShownIndex, setTagDropdownShownIndex, slaNames, category,
  categoryDropdownShownIndex, setCategoryDropdownShownIndex, draggableProps,
}: IssueRowProps) => {
  const [issueName, setIssueName] = useState<string>(title);
  const [nameError, setNameError] = useState<string | null>(null);
  const [textFieldFocused, setTextFieldFocused] = useState<boolean>(false);

  const nameInvalid: boolean = !!nameError;
  const tagsInvalid: boolean = !(tags?.length > 0);

  useEffect(() => setIssueName(title), [title]);

  const remainingUserGroups: UserGroup[] = groups.filter((group: UserGroup) => !tags
    ?.includes(group.UserGroupId));

  const addTagIconDisabled: boolean = !(remainingUserGroups?.length > 0);

  const updateName = (text: string) => {
    setIssueName(text);
    if (text.trim().length === 0) {
      setNameError('Issue name cannot be empty');
    } else if (text.trim().toLowerCase() === 'other') {
      setNameError(`Issue name cannot be '${text}'`);
    } else {
      setNameError(null);
    }
  };

  const onBlur: () => void = () => {
    setTextFieldFocused(false);
    updateIssue(
      issueName,
      tags,
      priority,
      enabled,
      category,
    );
    hideDropdowns();
  };

  const onFocus: () => void = () => {
    setTextFieldFocused(true);
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex(null);
    setIssueTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  const togglePriorityMenu: () => void = () => {
    setIssueTagDropdownShownIndex(null);
    setPriorityDropdownShownIndex(priorityDropdownShownIndex === index ? null
      : index);
    setTagDropdownShownIndex(-1);
    setCategoryDropdownShownIndex(null);
  };

  const toggleCategoryMenu: () => void = () => {
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex(-1);
    setIssueTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(categoryDropdownShownIndex === index ? null : index);
  };

  const toggleChangeGroupMenu = (itemIndex: number): void => {
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex((index === issueTagDropdownShownIndex
      && tagDropdownShownIndex === itemIndex) ? -1 : itemIndex);
    setIssueTagDropdownShownIndex(index);
    setCategoryDropdownShownIndex(null);
  };

  const pressAddTag: () => void = () => {
    logEvent('IssueRow', 'usergroup_plus');
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex((index === issueTagDropdownShownIndex
      && tagDropdownShownIndex === (tags?.length ?? 0)) ? -1 : tags?.length ?? 0);
    setIssueTagDropdownShownIndex(index);
    setCategoryDropdownShownIndex(null);
  };

  const hideDropdowns: () => void = () => {
    setPriorityDropdownShownIndex(null);
    setTagDropdownShownIndex(-1);
    setIssueTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  return <View style={[Style.row, styles.container, { zIndex: 10 - index }]}>
    <View style={styles.box}>
      <View
        style={[
          styles.disabledIndicator, enabled && styles.enabledIndicator,
          {
            borderTopStartRadius: styles.box.borderRadius,
            borderTopEndRadius: styles.box.borderRadius,
          },
        ]}
      />
      <View style={styles.boxContent}>
        <View style={Style.row}>
          <View style={Style.row}>
            {!isMobile && <div
              style={{ cursor: draggableProps?.isDragging ? 'grabbing' : 'grab' }}
              {...draggableProps?.attributes}
              {...draggableProps?.listeners}
              onMouseDown={hideDropdowns}
            >
              <ReorderIcon24 style={styles.reorderIcon} />
            </div>}
            <Switch
              toggled={enabled}
              toggle={() => {
                logEvent('IssueRow', 'switch');
                updateIssue(title, tags, priority, !enabled, category);
                hideDropdowns();
              }}
              testID='am-issue-toggle-btn'
            />
          </View>
          <PressableStyled
            onPress={() => {
              hideDropdowns();
              deleteIssue();
            }}
            testID='am-delete-issue-btn'
          >
            <DeleteIcon24 />
          </PressableStyled>
        </View>
        <View style={[Style.row, { zIndex: 4 }]}>
          <View style={[Style.row, { flex: 1 }]}>
            <View style={{ flex: 1 }}>
              <TextInput
                placeholder='Enter issue'
                placeholderTextColor={Colors.light.hereworksBlack400}
                value={issueName}
                onChangeText={updateName}
                {...{ onBlur, onFocus }}
                onEndEditing={onBlur}
                onSubmitEditing={onBlur}
                style={[
                  styles.textInput,
                  textFieldFocused && styles.textInputFocused,
                  nameInvalid && { borderColor: styles.errorText.color },
                ]}
                testID='am-issue-name-input-field'
              />
            </View>
            <View style={styles.prioritySelector}>
              {slaNames.map((_, itemIndex: number) => itemIndex)
                .includes(parseInt(`${priority}`, 10)) ? <Pressable
                  onPress={togglePriorityMenu}
                  style={[
                    styles.priorityOption,
                    Style.row, { borderColor: getPriorityColor(priority) },
                  ]}
                  testID='am-issue-priority-dropdown-menu'
                >
                <PriorityFlag color={getPriorityColor(priority)} />
                <Text style={[styles.priorityText, { color: getPriorityColor(priority) }]}>
                  {`P${priority + 1}`}
                </Text>
                <DropdownIcon
                  color={getPriorityColor(priority)}
                  style={priorityDropdownShownIndex === index ? Style.inverted : Style.upright}
                />
              </Pressable> : <PressableStyled onPress={togglePriorityMenu}>
                <PlusIcon20 />
              </PressableStyled>}
              <View>
                {!isMobile && priorityDropdownShownIndex === index
                  && <View style={[styles.dropdown, styles.priorityDropdown]}>
                    <PriorityList
                      {...{ slaNames, priority }}
                      update={(itemIndex: number) => {
                        updateIssue(title, tags, itemIndex, enabled, category);
                        hideDropdowns();
                      }}
                    />
                  </View>}
              </View>
            </View>
          </View>
        </View>
        {nameInvalid && <Text style={styles.errorText}>{nameError}</Text>}
        {groups?.length ? <View style={styles.tagSection}>
          {(typeof tags === 'object' && tags?.length) ? tags
            ?.map((tag: string, itemIndex: number) => <UserGroupTag
              {...{
                tag,
                itemIndex,
                index,
                tagDropdownShownIndex,
                issueTagDropdownShownIndex,
                tags,
                groups,
                enabled,
                title,
                priority,
                setTagDropdownShownIndex,
                setIssueTagDropdownShownIndex,
                addTagIconDisabled,
                updateIssue,
                hideDropdowns,
                toggleChangeGroupMenu,
              }}
              key={`user_group_tag_${itemIndex}_${tag}`}
            />)
            : null}
          <View>
            <AssignUserGroupButton
              onPress={pressAddTag}
              disabled={addTagIconDisabled}
              invalid={tagsInvalid}
            />
            {!isMobile && issueTagDropdownShownIndex === index
              && tagDropdownShownIndex === (tags?.length ?? 0) && <ScrollView
                style={[
                  styles.dropdown, styles.tagDropdown,
                ]}
                nestedScrollEnabled
              >
                <AddGroupList
                  {...{ tags, groups }}
                  update={(userGroupId: string) => {
                    setIssueTagDropdownShownIndex(null);
                    const updatedTags: string[] = tags ?? [];
                    updatedTags.push(userGroupId);
                    updateIssue(title, updatedTags, priority, enabled, category);
                    hideDropdowns();
                  }}
                />
              </ScrollView>}
          </View>
        </View> : null}
        {tagsInvalid && groups?.length && (
          <Text style={styles.errorText}>
            {'A user group must be assigned to an issue'}
          </Text>
        )}
        <Pressable
          style={[Style.row, styles.categoryButton]}
          onPress={toggleCategoryMenu}
        >
          <Text style={styles.text}>
            {TemplateIssueCategoryTitles[category] ?? 'Select a category'}
          </Text>
          <FontAwesome
            name={index === categoryDropdownShownIndex ? 'angle-up' : 'angle-down'}
            size={24}
            color={Colors.light.hereworksBlue200}
          />
        </Pressable>
        <View>
          {!isMobile && index === categoryDropdownShownIndex && <ScrollView
            style={[styles.dropdown, styles.categoryDropdown]}
            nestedScrollEnabled
          >
            <CategoryList
              category={category}
              update={(newCategory: TemplateIssueCategory) => {
                updateIssue(title, tags, priority, enabled, newCategory);
                hideDropdowns();
              }}
            />
          </ScrollView>}
        </View>
      </View>
    </View>
    {isMobile ? <>
      <SelectionModal
        listContent={<PriorityList
          {...{ slaNames, priority }}
          update={(itemIndex: number) => {
            updateIssue(title, tags, itemIndex, enabled, category);
            hideDropdowns();
          }}
        />}
        title='Choose priority'
        isVisible={priorityDropdownShownIndex === index}
        hide={hideDropdowns}
      />
      <SelectionModal
        listContent={<AddGroupList
          {...{ tags, groups }}
          update={(userGroupId: string) => {
            const updatedTags: string[] = tags ?? [];
            updatedTags.push(userGroupId);
            updateIssue(title, updatedTags, priority, enabled, category);
            hideDropdowns();
          }}
        />}
        title='Select user group'
        isVisible={tagDropdownShownIndex === (tags?.length ?? 0)
          && issueTagDropdownShownIndex === index}
        hide={hideDropdowns}
      />
      <SelectionModal
        listContent={<CategoryList
          {...{ category }}
          update={(newCategory: TemplateIssueCategory) => {
            updateIssue(title, tags, priority, enabled, newCategory);
            hideDropdowns();
          }}
        />}
        title='Select category'
        isVisible={categoryDropdownShownIndex === index}
        hide={hideDropdowns}
      />
    </> : null}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
  },
  disabledIndicator: {
    backgroundColor: Colors.light.hereworksDarkBlue100,
    height: 4,
    width: '100%',
  },
  enabledIndicator: {
    backgroundColor: Colors.light.hereworksGreen400,
  },
  box: {
    borderRadius: 10,
    backgroundColor: Colors.light.hereworksWhite100,
    flex: 1,
    marginEnd: isMobile ? 10 : undefined,
    zIndex: 95,
  },
  boxContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flex: 1,
  },
  prioritySelector: {
    position: 'absolute',
    end: 0,
    zIndex: 10,
  },
  priorityOption: {
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: 4,
    marginEnd: 8,
    backgroundColor: '#FFF',
  },
  priorityText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    marginHorizontal: 2,
  },
  textInput: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    flex: 1,
    flexWrap: 'wrap',
    paddingStart: 10,
    paddingEnd: 40,
    paddingBottom: 8,
    paddingTop: 8,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    marginTop: 8,
    marginBottom: 4,
    backgroundColor: '#FFF',
    outlineWidth: 0,
  },
  textInputFocused: {
    borderColor: Colors.light.hereworksBlue400,
  },
  tagSection: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    zIndex: 3,
  },
  dropdown: {
    position: 'absolute',
    width: 170,
    borderWidth: 1.5,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    alignSelf: 'center',
    overflow: 'hidden',
    zIndex: 20,
  },
  priorityDropdown: {
    top: 4,
    end: 0,
  },
  tagDropdown: {
    position: Platform.OS === 'android' ? 'relative' : 'absolute',
    start: 0,
    top: Platform.OS === 'android' ? undefined : 38,
    marginVertical: Platform.OS === 'android' ? 4 : undefined,
    maxHeight: 180,
    alignSelf: 'flex-start',
  },
  categoryDropdown: {
    height: 200,
    width: '100%',
    marginTop: 4,
  },
  categoryButton: {
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    backgroundColor: '#FFF',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    flex: 1,
  },
  text: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
  },
  reorderIcon: {
    marginEnd: 8,
  },
});

export default IssueRow;
